"use client";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { TRANSFORMER_MAX_MOBILE_WIDTH_PX } from "../../../utils/constants.js";
import { mergeRefs } from "../../../utils/mergeRefs.js";
import { InputButton } from "../../InputButton/index.js";
import { CustomMonth } from "../CustomComponents/CustomMonth.js";
import { Footer } from "../CustomComponents/Footer.js";
import { getDefaultMonth, getFormattedDates, shouldAutoClose } from "../helpers/helpers.js";
import { DatePickerDesktop } from "./DatePickerDesktop.js";
import { DatePickerMobile } from "./DatePickerMobile.js";
const DEFAULT_DATE_PICKER_FORMAT = 'ccc, MMM d';
const MOBILE_NUMBER_OF_MONTHS = 12;
function createDatePickerFooterComponent(props) {
    return function() {
        return /*#__PURE__*/ jsx(Footer, {
            ...props
        });
    };
}
function DatePickerWrapper({ mode = 'single', handleCalendarChange, footerLeftNode, subHeading, components: initialComponents, label, selected, initialSelected, displayNights, isOpen: isCalendarOpen = false, customDateFormat, defaultMonth: defaultMonthProp, renderTrigger, onOpenChange, numberOfMonths, placeholder, isDisabled, errorMessage, helperText, errorText, inputRef, ...restProps }) {
    const [isOpen, setIsOpen] = useState(isCalendarOpen);
    const showDrawerOverlay = useMediaQuery({
        maxWidth: TRANSFORMER_MAX_MOBILE_WIDTH_PX
    });
    const setOpen = useCallback((open)=>{
        setIsOpen(open);
        onOpenChange?.(open);
    }, [
        onOpenChange
    ]);
    const initialFormattedDates = useMemo(()=>{
        if (subHeading) return subHeading;
        const datesToFormat = initialSelected ?? selected;
        if (datesToFormat) return getFormattedDates({
            dates: datesToFormat,
            dateFormat: DEFAULT_DATE_PICKER_FORMAT,
            displayNights
        });
        return '';
    }, [
        initialSelected,
        selected,
        subHeading,
        displayNights
    ]);
    const [calendarState, setCalendarState] = useState({
        selectedDates: initialSelected ?? selected,
        defaultMonth: getDefaultMonth({
            mode,
            selected: initialSelected ?? selected
        }) ?? defaultMonthProp,
        formattedDates: initialFormattedDates
    });
    const selectedKey = useMemo(()=>{
        if (!selected) return '';
        if (selected instanceof Date) return selected.getTime().toString();
        if ('from' in selected) return `${selected.from?.getTime() ?? ''}-${selected.to?.getTime() ?? ''}`;
        if (Array.isArray(selected)) return selected.map((d)=>d.getTime()).join(',');
        return '';
    }, [
        selected
    ]);
    const prevSelectedKeyRef = useRef(selectedKey);
    useEffect(()=>{
        if (initialSelected || isOpen) return;
        if (selectedKey === prevSelectedKeyRef.current) return;
        prevSelectedKeyRef.current = selectedKey;
        setCalendarState({
            selectedDates: selected,
            defaultMonth: getDefaultMonth({
                mode,
                selected
            }) ?? defaultMonthProp,
            formattedDates: selected ? getFormattedDates({
                dates: selected,
                dateFormat: customDateFormat ?? DEFAULT_DATE_PICKER_FORMAT,
                displayNights
            }) : ''
        });
    }, [
        customDateFormat,
        defaultMonthProp,
        displayNights,
        initialSelected,
        isOpen,
        mode,
        selected,
        selectedKey
    ]);
    const handleCalendarDates = useCallback((newDates, triggerDate)=>{
        setCalendarState((prev)=>({
                ...prev,
                selectedDates: newDates,
                defaultMonth: getDefaultMonth({
                    mode,
                    selected: newDates
                }) ?? defaultMonthProp,
                formattedDates: getFormattedDates({
                    dates: newDates,
                    dateFormat: customDateFormat ?? DEFAULT_DATE_PICKER_FORMAT,
                    displayNights
                })
            }));
        handleCalendarChange(newDates, triggerDate);
        if (shouldAutoClose(mode, newDates)) setOpen(false);
    }, [
        mode,
        defaultMonthProp,
        customDateFormat,
        displayNights,
        handleCalendarChange,
        setOpen
    ]);
    const value = useMemo(()=>subHeading || calendarState.formattedDates || '', [
        subHeading,
        calendarState.formattedDates
    ]);
    const subHeadingValue = useMemo(()=>subHeading || calendarState.formattedDates || label || '', [
        subHeading,
        calendarState.formattedDates,
        label
    ]);
    const InputTrigger = useCallback(({ onClick, ref, ...props })=>{
        if (renderTrigger) return renderTrigger({
            ...props,
            onClick,
            ref,
            isOpen
        });
        return /*#__PURE__*/ jsx(InputButton, {
            ...props,
            ref: mergeRefs(ref, inputRef),
            "data-testid": "date-picker-input",
            className: "w-full",
            label: label,
            value: value,
            iconLeft: 'calendar_month',
            size: "lg",
            placeholder: placeholder,
            isDisabled: isDisabled,
            errorMessage: errorMessage,
            helperText: helperText,
            errorText: errorText,
            dataFocusWithin: isOpen,
            onClick: onClick
        });
    }, [
        errorMessage,
        errorText,
        helperText,
        isDisabled,
        inputRef,
        isOpen,
        label,
        placeholder,
        renderTrigger,
        value
    ]);
    const desktopComponents = useMemo(()=>({
            ...initialComponents,
            Month: CustomMonth,
            Footer: createDatePickerFooterComponent({
                footerLeftNode,
                handleFooterCallBack: setOpen
            })
        }), [
        initialComponents,
        footerLeftNode,
        setOpen
    ]);
    const mobileComponents = useMemo(()=>({
            ...initialComponents,
            Month: CustomMonth
        }), [
        initialComponents
    ]);
    const { selectedDates, defaultMonth } = calendarState;
    const props = {
        ...restProps,
        mode,
        open: isOpen,
        handleCalendarChange: handleCalendarDates,
        selected: selectedDates,
        defaultMonth
    };
    return showDrawerOverlay ? /*#__PURE__*/ jsxs(Fragment, {
        children: [
            InputTrigger({
                onClick: ()=>setOpen(true)
            }),
            /*#__PURE__*/ jsx(DatePickerMobile, {
                ...props,
                numberOfMonths: MOBILE_NUMBER_OF_MONTHS,
                isMobile: true,
                handleDrawerState: setOpen,
                subHeading: subHeadingValue,
                autoFocus: true,
                components: mobileComponents
            })
        ]
    }) : /*#__PURE__*/ jsx(DatePickerDesktop, {
        ...props,
        numberOfMonths: numberOfMonths,
        handlePopOverOpenState: setOpen,
        inputNode: InputTrigger,
        title: label,
        components: desktopComponents
    });
}
const DatePicker = /*#__PURE__*/ memo(DatePickerWrapper);
export { DEFAULT_DATE_PICKER_FORMAT, DatePicker };
